// constants/images.ts
import type { EventType } from '@/constants/geo';

type Mode = 'dark' | 'light';
export type DeviceSize = 'mobile' | 'desktop';
type BackgroundImageSet = Record<Mode, Record<DeviceSize, string[]>>;
type BackgroundImageCandidate = {
  url: string;
  width: number;
};

const hennaBaseUrl =
  'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImageHenna';

const weddingBaseUrl =
  'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImageWedding';

const backgroundImageVersion = '20260618-1';
const localBackgroundImageVersion = '20260808-1';
const hennaOptimizedMobileHeroCandidates: BackgroundImageCandidate[] = [
  { url: '/images/backgrounds/henna/mobileDark1-640-q72.webp', width: 640 },
  { url: '/images/backgrounds/henna/mobileDark1-768-q72.webp', width: 768 },
];
const hennaOptimizedDesktopHeroCandidates: BackgroundImageCandidate[] = [
  { url: '/images/backgrounds/henna/desktopDark1-1280-q72.webp', width: 1280 },
  { url: '/images/backgrounds/henna/desktopDark1-1440-q72.webp', width: 1440 },
  { url: '/images/backgrounds/henna/desktopDark1-1536-q72.webp', width: 1536 },
];

const getHennaOptimizedMobileDarkImage = (index: number): string => {
  const imageNumber = index + 1;

  if (imageNumber >= 1 && imageNumber <= 7) {
    return `/images/backgrounds/henna/mobileDark${imageNumber}-768-q72.webp`;
  }

  return `${hennaBaseUrl}/mobileDark${imageNumber}.webp`;
};

const getHennaOptimizedDesktopDarkImage = (index: number): string => {
  const imageNumber = index + 1;

  if (imageNumber >= 1 && imageNumber <= 7) {
    return `/images/backgrounds/henna/desktopDark${imageNumber}-1440-q72.webp`;
  }

  return `${hennaBaseUrl}/desktopDark${imageNumber}.webp`;
};

const withBackgroundVersion = (url: string): string => {
  const version = url.startsWith('/images/backgrounds/')
    ? localBackgroundImageVersion
    : backgroundImageVersion;

  return `${url}${url.includes('?') ? '&' : '?'}v=${version}`;
};

const getHennaMobileDarkImage = (index: number): string => {
  return getHennaOptimizedMobileDarkImage(index);
};

const getHennaMobileDarkImageCandidates = (
  index: number,
): BackgroundImageCandidate[] | undefined => {
  if (index === 0) return hennaOptimizedMobileHeroCandidates;

  const imageNumber = index + 1;
  if (imageNumber < 1 || imageNumber > 7) return undefined;

  return [
    {
      url: getHennaOptimizedMobileDarkImage(index),
      width: 768,
    },
  ];
};

const getHennaDesktopDarkImageCandidates = (
  index: number,
): BackgroundImageCandidate[] | undefined => {
  if (index === 0) return hennaOptimizedDesktopHeroCandidates;

  const imageNumber = index + 1;
  if (imageNumber < 1 || imageNumber > 7) return undefined;

  return [
    {
      url: getHennaOptimizedDesktopDarkImage(index),
      width: 1440,
    },
  ];
};

const buildBackgroundImageSrcSet = (
  candidates: readonly BackgroundImageCandidate[],
): string =>
  candidates
    .map(candidate => `${withBackgroundVersion(candidate.url)} ${candidate.width}w`)
    .join(', ');

const defaultBackgroundImages: BackgroundImageSet = {
  dark: {
    mobile: [
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileDarkSection12.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileDarkSection13.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileDarkSection10.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileDarkSection11.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileDarkSection13.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileDarkSection10.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileDarkSection11.webp',
    ],
    desktop: [
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopDarkSection10.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopDarkSection11.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopDarkSection12.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopDarkSection13.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopDarkSection11.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopDarkSection12.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopDarkSection13.webp',
    ],
  },
  light: {
    mobile: [
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileLightSection6.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileLightSection10.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileLightSection12.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileLightSection14.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileLightSection10.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileLightSection12.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileLightSection14.webp',

    ],
    desktop: [
     // 'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/new4.png',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopLightSection7.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopLightSection8.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopLightSection9.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopLightSection10.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopLightSection8.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopLightSection9.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopLightSection10.webp',
    ],
  },
};

const cloneBackgroundImages = (images: BackgroundImageSet): BackgroundImageSet => ({
  dark: {
    mobile: images.dark.mobile.map(withBackgroundVersion),
    desktop: images.dark.desktop.map(withBackgroundVersion),
  },
  light: {
    mobile: images.light.mobile.map(withBackgroundVersion),
    desktop: images.light.desktop.map(withBackgroundVersion),
  },
});

const hennaBackgroundImages: BackgroundImageSet = {
  dark: {
    mobile: Array.from(
      { length: 7 },
      (_, index) => withBackgroundVersion(getHennaMobileDarkImage(index))
    ),
    desktop: Array.from(
      { length: 7 },
      (_, index) => withBackgroundVersion(getHennaOptimizedDesktopDarkImage(index))
    ),
  },
  light: {
    mobile: Array.from(
      { length: 7 },
      (_, index) => withBackgroundVersion(`${hennaBaseUrl}/mobileLight${index + 1}.webp`)
    ),
    desktop: Array.from(
      { length: 7 },
      (_, index) => withBackgroundVersion(`${hennaBaseUrl}/desktopLight${index + 1}.webp`)
    ),
  },
};

const weddingBackgroundImages: BackgroundImageSet = {
  dark: {
    mobile: Array.from(
      { length: 7 },
      (_, index) =>
        withBackgroundVersion(
          `${weddingBaseUrl}/mobileDark${index + 1}${index === 0 ? '-800' : ''}.webp`
        )
    ),
    desktop: Array.from(
      { length: 7 },
      (_, index) => withBackgroundVersion(`${weddingBaseUrl}/desktopDark${index + 1}.webp`)
    ),
  },
  light: {
    mobile: Array.from(
      { length: 7 },
      (_, index) => withBackgroundVersion(`${weddingBaseUrl}/mobileLight${index + 1}.webp`)
    ),
    desktop: Array.from(
      { length: 7 },
      (_, index) => withBackgroundVersion(`${weddingBaseUrl}/desktopLight${index + 1}.webp`)
    ),
  },
};

export const backgroundImagesByEvent: Record<EventType, BackgroundImageSet> = {
  'אירוע': weddingBackgroundImages,
  'חתונה': weddingBackgroundImages,
  'חינה': hennaBackgroundImages,
  'בר-מצווה': weddingBackgroundImages,
};

export const getBackgroundImagesForEvent = (
  eventType: EventType = 'אירוע'
): BackgroundImageSet => backgroundImagesByEvent[eventType] ?? cloneBackgroundImages(defaultBackgroundImages);

export const getBackgroundImageSrcSetForEvent = (
  eventType: EventType = 'אירוע',
  mode: Mode = 'dark',
  size: DeviceSize = 'desktop',
  index = 0,
): string | undefined => {
  if (eventType !== 'חינה' || mode !== 'dark') {
    return undefined;
  }

  const candidates =
    size === 'mobile'
      ? getHennaMobileDarkImageCandidates(index)
      : getHennaDesktopDarkImageCandidates(index);

  return candidates ? buildBackgroundImageSrcSet(candidates) : undefined;
};

export const backgroundImages = cloneBackgroundImages(defaultBackgroundImages);
